import React, { useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer } from "../hrForm";
import { EmptyState, LoadState, PrimaryButton } from "../hrComponents";
import { cardStyle, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../hrUi";
import { defaultPayMonth, formatMoney } from "./payrollHelpers";

// PAY-04: one-time earnings, deductions and reimbursements (a bonus, a fine, a claim paid back). The next run for the month picks them up.
const KINDS = [{ label: "Earning (bonus, incentive)", value: "EARNING" }, { label: "Deduction (fine, recovery)", value: "DEDUCTION" }, { label: "Reimbursement", value: "REIMBURSEMENT" }];

export default function PayrollInputsTab() {
  const [month, setMonth] = useState(defaultPayMonth());
  const [open, setOpen] = useState(false);
  const { data, loading, error, reload } = useLoad(() => hrmsService.listPayrollInputs({ pay_month: month }), [month]);
  const rows = data || [];
  const remove = async (id) => {
    const res = await hrmsService.voidPayrollInput(id);
    if (res?.success) {
      toast.success("Removed");
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };
  return (
    <>
      <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, display: "block" }} htmlFor="inputs-month">
            Month
          </label>
          <input id="inputs-month" type="month" className="form-control" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        </div>
        <PrimaryButton onClick={() => setOpen(true)}>Add one-time item</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="Nothing for this month" text="Add a bonus, a fine or a repayment and the run for this month will include it." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {["Employee", "What", "Kind", "Amount", "Status", ""].map((h, i) => (
                    <th key={i} style={{ ...thStyle, textAlign: h === "Amount" ? "right" : "left" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>
                      {r.full_name}
                      <div style={{ fontSize: 12, color: "#6B7280" }}>{r.employee_code}</div>
                    </td>
                    <td style={tdStyle}>
                      {r.name}
                      {r.note ? <div style={{ fontSize: 12, color: "#6B7280" }}>{r.note}</div> : null}
                    </td>
                    <td style={tdStyle}>{statusLabel(r.kind)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatMoney(r.amount)}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(r.status === "CONSUMED" ? "APPROVED" : r.status)}>{r.status === "CONSUMED" ? "Paid" : statusLabel(r.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {r.status === "OPEN" ? (
                        <button type="button" style={{ ...iconBtnStyle, fontSize: 13, fontWeight: 600 }} onClick={() => remove(r.id)}>
                          Remove
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={open}
        title="Add one-time item"
        initial={{ pay_month: month, kind: "EARNING" }}
        fields={[
          { key: "employee_id", label: "Employee", type: "employee", required: true },
          { key: "pay_month", label: "Pay in the run for", type: "month", required: true },
          { key: "kind", label: "Kind", type: "select", options: KINDS },
          { key: "name", label: "What it is for", required: true, max: 100, hint: "Shown on the payslip, e.g. Diwali bonus" },
          { key: "amount", label: "Amount", type: "number", step: "0.01", min: "0", required: true },
          { key: "note", label: "Note", max: 200 },
        ]}
        onHide={() => setOpen(false)}
        onSave={(clean) => hrmsService.createPayrollInput(clean)}
        onDone={reload}
      />
    </>
  );
}
